import axios, { AxiosResponse } from 'axios'
import { fetchJwt } from '.'
import { Maybe, UploadFile, UsersPermissionsUserEntity } from '../generated/graphql'
import omit from 'lodash/omit'

const BASE = process.env.REACT_APP_API_HOST || ''

export type StrapiLoginPayload = {
  jwt: string
  user: UsersPermissionsUserEntity
}

/**
 * Optional PIA registration context captured by the guided onboarding wizard and
 * forwarded to the Strapi register endpoint.
 */
export type RegisterExtraPayload = {
  piaOnboarding?: Record<string, unknown>
  organisationProfile?: Record<string, unknown>
}

const downloadCsv = async (filterValues: any) => {
  const where = {
    ...omit(filterValues, 'task_definition', 'date_range', 'baorider'),
    ...(filterValues.date_range && {
      created_at_gte: (filterValues.date_range as any)[0] || undefined,
      created_at_lte: (filterValues.date_range as any)[1] || undefined
    }),
    ...(filterValues.task_definition && {
      'task_definition.category': (filterValues.task_definition as any).category || undefined,
      'task_definition.task_type': (filterValues.task_definition as any).task_type || undefined
    }),
    project: filterValues?.project || undefined,
    baorider: filterValues.baorider || undefined
  }
  try {
    const result = await axios.post(
      BASE + '/export-tasks',
      {
        data: JSON.stringify(where)
      },
      {
        headers: {
          Authorization: `Bearer ${fetchJwt()}`
        }
      }
    )

    const blob = new Blob([result.data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = `tasks(${new Date().toLocaleDateString()}).csv`
    link.click()
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Build and download an .xlsx of the exported task rows.
 *
 * SheetJS is loaded on demand. It used to be a top-level `import`, and this
 * module is reached from `AuthProvider` — i.e. on the very first render — so
 * every visitor downloaded the whole spreadsheet library before they could see
 * the login form, whether or not they ever exported anything.
 */
const downloadExcel = async (result: Record<string, any[]>) => {
  const XLSX = await import('xlsx')

  return new Promise((resolve, reject) => {
    try {
      if (!result || typeof result !== 'object') {
        throw new Error('Invalid result data.')
      }

      const wb = XLSX.utils.book_new()

      /**
       * Excel rejects sheet names longer than 31 characters, names containing
       * any of `\ / ? * [ ]`, and duplicates — `book_append_sheet` throws on
       * each, which aborted the whole export rather than one sheet. Task-type
       * titles are customer-authored and routinely hit all three.
       */
      const usedSheetNames = new Set<string>()
      const toSheetName = (rawName: string) => {
        const cleaned = (rawName || 'Sheet').replace(/[\\/?*[\]:]/g, ' ').trim() || 'Sheet'
        let candidate = cleaned.slice(0, 31)

        for (let suffix = 2; usedSheetNames.has(candidate.toLowerCase()); suffix += 1) {
          const tag = ` (${suffix})`
          candidate = `${cleaned.slice(0, 31 - tag.length)}${tag}`
        }

        usedSheetNames.add(candidate.toLowerCase())
        return candidate
      }

      Object.keys(result).forEach((key) => {
        const data = result[key] || []

        if (!Array.isArray(data) || data.length === 0) return

        // Extract unique columns and control order
        let columns = Object.keys(data.reduce((acc, obj) => Object.assign(acc, obj), {}))
        const columnsToRemove = ['latitude', 'longitude', 'accuracy']

        columns = columns.filter((col) => !columnsToRemove.includes(col)).concat(columnsToRemove)

        // Ensure all numeric fields are formatted as numbers
        const formattedData = data.map((item) =>
          columns.reduce(
            (formatted, column) => {
              const value = item[column]
              if (typeof value === 'number') {
                formatted[column] = { t: 'n', v: value } // Format as Excel number
              } else {
                formatted[column] = value // Retain other types as is
              }
              return formatted
            },
            {} as Record<string, any>
          )
        )

        // Create sheet
        const ws = XLSX.utils.json_to_sheet(formattedData, { header: columns })
        XLSX.utils.book_append_sheet(wb, ws, toSheetName(key.split('::')[1] || key))
      })

      // SheetJS throws "Workbook is empty" when every group was skipped above,
      // which surfaced to the customer as an opaque failure.
      if (!wb.SheetNames.length) {
        throw new Error('No task data matched the current filters.')
      }

      // Write file
      const fileName = `tasks(${new Date().toISOString().split('T')[0]}).xlsx`
      XLSX.writeFile(wb, fileName)

      resolve(true)
    } catch (error) {
      reject(error)
    }
  })
}

const downloadBaoriderCsv = async (
  baoriders: Maybe<UsersPermissionsUserEntity>[],
  filename: string
) => {
  try {
    const result = await axios.post(
      BASE + '/export-baoriders',
      { baoriders },
      {
        headers: {
          Authorization: `Bearer ${fetchJwt()}`
        }
      }
    )
    const blob = new Blob([result.data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = filename + '.csv'
    link.click()
  } catch (error) {
    return Promise.reject(error)
  }
}

const downloadCommunityMemberCsv = async (communityID: string) => {
  try {
    const result = await axios.post(
      BASE + '/export-communitymembers',
      {
        communityID
      },
      {
        headers: {
          Authorization: `Bearer ${fetchJwt()}`
        }
      }
    )
    const blob = new Blob([result.data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = 'Commnuity Members.csv'
    link.click()
  } catch (error) {
    return Promise.reject(error)
  }
}

const forgotPassword = async (email: string): Promise<AxiosResponse<any>> => {
  try {
    return await axios.post(BASE + '/api/auth/forgot-password', {
      email
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const resetPassword = async (code: string, password: string, passwordConfirmation: string) => {
  try {
    return await axios.post(BASE + '/api/auth/reset-password', {
      code,
      password,
      passwordConfirmation
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const login = async (
  identifier: string,
  password: string
): Promise<AxiosResponse<StrapiLoginPayload>> => {
  try {
    return await axios.post(BASE + '/api/auth/local', {
      identifier,
      password,
      type: 'baoleader'
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const register = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  companyName: string,
  extra?: RegisterExtraPayload
): Promise<AxiosResponse<StrapiLoginPayload>> => {
  try {
    return await axios.post(BASE + '/api/auth/local/register', {
      firstName,
      lastName,
      email,
      password,
      companyName,
      ...(extra || {})
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const upload = async (
  file: File,
  onUploadProgress?: (progressEvent: any, file: File) => void
): Promise<AxiosResponse<UploadFile[]>> => {
  const formData = new FormData()
  formData.append('files', file)
  try {
    return await axios.post(BASE + '/api/upload', formData, {
      onUploadProgress: (event) => onUploadProgress && onUploadProgress(event, file),
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await fetchJwt()}`
      }
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const createCheckoutSession = async (priceId: string): Promise<AxiosResponse<any>> => {
  try {
    return await axios.post(
      `${BASE}/companies/create-checkout-session`,
      { priceId },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await fetchJwt()}`
        }
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

const createPaymentIntent = async (): Promise<AxiosResponse<any>> => {
  try {
    return axios.post(
      `${BASE}/companies/create-payment-intent`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await fetchJwt()}`
        }
      }
    )
  } catch (error) {
    return Promise.reject(error)
  }
}

const sendVerificationEmail = async (userId: number): Promise<AxiosResponse<any>> => {
  try {
    return await axios.post(BASE + '/api/auth/send-email-confirmation', {
      userId
    })
  } catch (error) {
    return Promise.reject(error)
  }
}

const confirmEmail = async (u: number, pin: string): Promise<AxiosResponse<any>> => {
  try {
    return await axios.get(BASE + `/api/auth/email-confirmation?u=${u}&pin=${pin}`)
  } catch (error) {
    return Promise.reject(error)
  }
}

export type SendEmailOtpResponse = {
  sent: boolean
  expiresAt?: string
}

export type VerifyEmailOtpResponse = {
  verified: boolean
}

/**
 * Pre-account email verification (anti-spam). Sends a 6-digit one-time code to the
 * business email captured in the guided onboarding wizard.
 */
const sendEmailOtp = async (email: string): Promise<AxiosResponse<SendEmailOtpResponse>> => {
  try {
    return await axios.post(BASE + '/api/auth/send-email-otp', { email })
  } catch (error) {
    return Promise.reject(error)
  }
}

/**
 * Verifies the 6-digit one-time code the user entered against the code sent to
 * their email during the guided onboarding wizard.
 */
const verifyEmailOtp = async (
  email: string,
  code: string
): Promise<AxiosResponse<VerifyEmailOtpResponse>> => {
  try {
    return await axios.post(BASE + '/api/auth/verify-email-otp', { email, code })
  } catch (error) {
    return Promise.reject(error)
  }
}

const crushObj = (obj: any): any =>
  Object.keys(obj).reduce(
    (acc, cur) =>
      typeof obj[cur] === 'object'
        ? { ...acc, ...crushObj(obj[cur]) }
        : { ...acc, [cur]: obj[cur] },
    {}
  )

const openBillingPortal = async () => {
  const res = await axios.post(
    `${BASE}/api/stripe-billing`,
    {},
    {
      headers: {
        Authorization: `Bearer ${await fetchJwt()}`
      }
    }
  )
  window.location.href = res?.data?.url
}

const handleCreateCheckoutSession = async () => {
  const res = await axios.post(
    `${BASE}/api/stripe-billing/create-checkout-session`,
    {},
    {
      headers: {
        Authorization: `Bearer ${await fetchJwt()}`
      }
    }
  )
  window.location.href = res?.data?.url
}

export default {
  openBillingPortal,
  forgotPassword,
  resetPassword,
  login,
  register,
  upload,
  createCheckoutSession,
  createPaymentIntent,
  downloadCsv,
  downloadExcel,
  downloadBaoriderCsv,
  downloadCommunityMemberCsv,
  sendVerificationEmail,
  confirmEmail,
  sendEmailOtp,
  verifyEmailOtp,
  crushObj,
  handleCreateCheckoutSession
}
